import type { CSSProperties, ReactNode } from "react";

/**
 * Sprint 18 Wave A — shared empty-state primitive.
 *
 * Replaces inline `EmptyState()` helpers across apps/web. Pages compose
 * this when a request succeeded but returned zero items, OR when the
 * user has not yet supplied filter input.
 *
 * Accessibility:
 *   - The block is a semantic `<section>` with an `aria-label` mirroring
 *     the title so screen readers can locate it via landmark navigation.
 *   - The optional decorative glyph is `aria-hidden`.
 *
 * Visual rules:
 *   - Centered column; `--gt-sys-text-muted` for body.
 *   - Optional CTA slot for "go create one" affordance.
 */
export interface EmptyStateProps {
  /** Visible heading. */
  title: string;
  /** Optional secondary description. */
  body?: ReactNode;
  /** Decorative glyph or icon. */
  icon?: ReactNode;
  /** Optional CTA button or link. */
  action?: ReactNode;
  padBlock?: CSSProperties["paddingBlock"];
  style?: CSSProperties;
}

export function EmptyState({ title, body, icon, action, padBlock, style }: EmptyStateProps) {
  return (
    <section
      aria-label={title}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "var(--gt-sys-space-3)",
        paddingBlock: padBlock ?? "var(--gt-sys-space-8)",
        textAlign: "center",
        ...style,
      }}
    >
      {icon ? (
        <span aria-hidden="true" style={{ fontSize: 40, color: "var(--gt-sys-text-muted)" }}>
          {icon}
        </span>
      ) : null}
      <strong style={{ fontSize: "var(--gt-sys-font-size-md)" }}>{title}</strong>
      {body ? (
        <span
          style={{
            color: "var(--gt-sys-text-muted)",
            fontSize: "var(--gt-sys-font-size-sm)",
            maxInlineSize: 420,
          }}
        >
          {body}
        </span>
      ) : null}
      {action ? <div>{action}</div> : null}
    </section>
  );
}
